<template>
  <div class="custom-fields-group tw-py-4 tw-border-t tw-border-b-0 tw-border-x-0 tw-border-solid tw-border-gray-200">
    <WdsStack justify-space-between align-center class="tw-pb-3">
      <WdsStack direction-column gap-xs>
        <WdsHeading type="title4">
          <WdsStack align-center gap-xs>
            <span class="tw-font-semibold">{{ title }}</span>
            <WdsTooltip v-if="tooltip" :text="tooltip">
              <WdsIcon :size="14" name="info" class="tw-text-gray-500" />
            </WdsTooltip>
          </WdsStack>
        </WdsHeading>
        <WdsText v-if="subtitle" secondary sm>{{ subtitle }}</WdsText>
      </WdsStack>
      <WdsStack align-center gap-md>
        <WdsText v-if="total > 0" secondary sm>
          {{ $t(':answered of :total answered', { answered, total }) }}
        </WdsText>
        <WdsButton
          v-if="editable"
          sm
          :href="editHref"
          :data-test="editDataTest"
          @click="emit('edit')"
        >
          <WdsStack align-center gap-xs>
            <WdsIcon :size="16" name="edit" />
            {{ $t('Edit') }}
          </WdsStack>
        </WdsButton>
      </WdsStack>
    </WdsStack>

    <WdsInput
      v-if="!hideSearch && total > 0"
      v-model="search"
      :placeholder="$t('Search...')"
      :prefix-icon="WdsIconNameEnum.SEARCH"
      data-test="custom-fields-search"
      class="tw-mb-3"
    />

    <WdsText v-if="searching && filteredSections.length === 0" secondary class="tw-px-2 tw-py-3">
      {{ $t('No custom fields match your search') }}
    </WdsText>

    <template v-for="section in filteredSections" :key="section.key">
      <WdsStack v-if="section.label" align-center gap-sm class="tw-px-2 tw-pt-4 tw-pb-1">
        <WdsText sm>
          <span class="tw-font-semibold">{{ section.label }}</span>
        </WdsText>
        <WdsText secondary sm>
          {{
            $t(':answered of :total', {
              answered: answeredIn(fullSection(section)),
              total: fullSection(section).fields.length,
            })
          }}
        </WdsText>
      </WdsStack>
      <CustomFieldValuesDisplay
        :custom-field-values="section.values"
        :fields="section.fields"
        plain
        class="tw-w-full"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsButton,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { FragmentType, useFragment } from '@/gql'
import { $t } from '@/composables/translation'
import CustomFieldValuesDisplay from '@/components/Fieldable/CustomFieldValuesDisplay.vue'
import {
  CUSTOM_FIELD_VALUES_VIEW_FRAGMENT,
  FieldableFormCustomFieldFragment,
} from '@/components/Fieldable/graphql/fragments'

export interface CustomFieldsSection {
  key: string
  /** Shown as a sub-heading with its own count; leave out for a single unlabelled list. */
  label?: string
  fields: FragmentType<typeof FieldableFormCustomFieldFragment>[]
  values: FragmentType<typeof CUSTOM_FIELD_VALUES_VIEW_FRAGMENT>[]
}

const props = defineProps<{
  title: string
  sections: CustomFieldsSection[]
  subtitle?: string
  tooltip?: string
  editable?: boolean
  editHref?: string
  editDataTest?: string
  hideSearch?: boolean
}>()

const emit = defineEmits<{ edit: [] }>()

const visibleSections = computed(() =>
  props.sections.filter((section) => section.fields.length > 0 || section.values.length > 0),
)

const search = ref('')
const searching = computed(() => search.value.trim() !== '')

const filteredSections = computed<CustomFieldsSection[]>(() => {
  if (!searching.value) return visibleSections.value

  const term = search.value.trim().toLowerCase()
  const matches = (text: string | null | undefined) => !!text && text.toLowerCase().includes(term)

  return visibleSections.value
    .map((section) => {
      const values = section.values.filter((value) => {
        const { displayValue, file, customField } = useFragment(
          CUSTOM_FIELD_VALUES_VIEW_FRAGMENT,
          value,
        )

        return matches(customField.title) || matches(displayValue) || matches(file?.name)
      })
      const matchedIds = new Set(
        values.map((value) => useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, value).customField.id),
      )
      const fields = section.fields.filter((field) => {
        const { id, title } = useFragment(FieldableFormCustomFieldFragment, field)

        return matches(title) || matchedIds.has(id)
      })

      return { ...section, fields, values }
    })
    .filter((section) => section.fields.length > 0 || section.values.length > 0)
})

const fullSection = (section: CustomFieldsSection) =>
  visibleSections.value.find(({ key }) => key === section.key) ?? section

const answeredIn = (section: CustomFieldsSection): number => {
  const answeredIds = new Set(
    section.values.map(
      (value) => useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, value).customField.id,
    ),
  )

  return section.fields.filter((field) =>
    answeredIds.has(useFragment(FieldableFormCustomFieldFragment, field).id),
  ).length
}

const total = computed(() =>
  visibleSections.value.reduce((sum, section) => sum + section.fields.length, 0),
)
const answered = computed(() =>
  visibleSections.value.reduce((sum, section) => sum + answeredIn(section), 0),
)
</script>

<style scoped lang="scss">
// The group's own top border separates it from the next one, so the last row needs none.
.custom-fields-group :deep(table:last-of-type tbody tr:last-child) {
  border-bottom-width: 0;
}
</style>
